import { FormController } from "./FormController";
import { FormWrapper } from "./FormWrapper";
import { GoogleServiceAccountForm } from "./GoogleServiceAccountKey";
import {
  activationKeyExample,
  amazonKeyExample,
  certificateExample,
  emailExample,
  passwordExample,
  rsaPrivateKey,
  standardKeyExample,
} from "./KeyInputPlaceholders";
import { RedshiftEditor } from "./RedshiftEditor";
import { SftpThings } from "./SftpThings";
import { SQLEditor } from "./SQLEditor";

const CREDENTIAL_TYPES = {
  acuity_ads: {
    display_name: "Acuity Ads",
    editor: (
      <FormWrapper<AcuityAdsType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  ad_juster_api: {
    display_name: "Ad-Juster API",
    editor: (
      <FormWrapper<ADJusterApiType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="key"
          placeholder={standardKeyExample}
          label="API Key"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="secret"
          label="API Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  adaptmx: {
    display_name: "AdaptMX",
    editor: (
      <FormWrapper<AdaptmxType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  adbook: {
    display_name: "Adbook",
    editor: (
      <FormWrapper<AdbookType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="key"
          placeholder={emailExample}
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="secret"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  adcolony_apikey: {
    display_name: "Adcolony API Key",
    editor: (
      <FormWrapper<AdcolonyType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  adform: {
    display_name: "Adform",
    editor: (
      <FormWrapper<Adform>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  adobe_oauth: {
    display_name: "Adobe OAuth",
    editor: (
      <FormWrapper<AdobeOauthType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="scopes"
          placeholder="https://www.googleapis.com/auth/cloud-platform"
          label="Scopes"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  adslot: {
    display_name: "AdSlot",
    editor: (
      <FormWrapper<AdSlotType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  adswizz: {
    display_name: "Adswizz",
    editor: (
      <FormWrapper<AdswizzType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  airtable: {
    display_name: "Airtable",
    editor: (
      <FormWrapper<AirtableType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_token"
          label="Access Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  amagi_analytics: {
    display_name: "Amagi Analytics",
    editor: (
      <FormWrapper<AmagiAnalyticsType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_token"
          label="Access Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  amazon_referrals: {
    display_name: "Amazon Referrals",
    editor: (
      <FormWrapper<AmazonType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="key"
          label="Username"
          placeholder="some_key"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="secret"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  amazon_seller: {
    display_name: "Amazon Seller",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth />,
  },
  apple_news: {
    display_name: "Apple News",
    editor: (
      <FormWrapper<AppleNewsType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="channel_id"
          placeholder="1234567890"
          label="Channel id"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="key_id"
          label="Key id"
          rules={{ required: true }}
        />

        <FormController
          elementType="textField"
          type="password"
          placeholder={standardKeyExample}
          fieldName="key_secret"
          label="Key Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  apple_news_publishers: {
    display_name: "Apple News Publishers",
    editor: (
      <FormWrapper<AppleNewsPublishersType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="certificate"
          placeholder={certificateExample}
          label="Certificate"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="private_key"
          placeholder={rsaPrivateKey}
          label="Private Key"
          rules={{ required: true }}
        />

        <FormController
          elementType="textField"
          type="password"
          placeholder={standardKeyExample}
          fieldName="token"
          label="Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  apple_search_ads_pubpriv_keys: {
    display_name: "Apple Search Ads Public/Private Key Pair",
    editor: (
      <FormWrapper<AppleSearchAdsType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="team_id"
          placeholder="ABC123XYZ9"
          label="Team ID"
          rules={{ required: true }}
        />

        <FormController
          elementType="textField"
          type="text"
          fieldName="key_id"
          label="Key ID"
          rules={{ required: true }}
        />

        <FormController
          elementType="textarea"
          type="password"
          fieldName="private_key"
          placeholder={rsaPrivateKey}
          label="Private Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  apple_search_ads_oauth: {
    display_name: "AppleOauth",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth />,
  },
  applovin_apikey: {
    display_name: "AppLovin",
    editor: (
      <FormWrapper<AppLovinKey>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  applovin_max: {
    display_name: "AppLovin Max",
    editor: (
      <FormWrapper<AppLovinKey>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  appnexus: {
    display_name: "AppNexus",
    editor: (
      <FormWrapper<AppNexusType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="username"
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  akamai_janrain: {
    display_name: "Akamai JanRain",
    editor: (
      <FormWrapper<AkamaiType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  avantlink_key: {
    display_name: "Avantlink Key",
    editor: (
      <FormWrapper<AvantlinkType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  aventri_key: {
    display_name: "Aventri",
    editor: (
      <FormWrapper<AventriType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="account_id"
          label="Account ID"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  awin: {
    display_name: "AWIN",
    editor: (
      <FormWrapper<AWINType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_token"
          label="API Access Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  aws: {
    display_name: "AWS (User)",
    editor: (
      <FormWrapper<AwsType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="aws_access_key_id"
          placeholder={amazonKeyExample}
          label="Account Id"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="aws_secret_access_key"
          placeholder={amazonKeyExample}
          label="AWS Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  aws_role: {
    display_name: "AWS (IAM Role)",
    editor: (
      <FormWrapper<AwsRoleType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="target_role_arn"
          placeholder="arn:aws:iam::123456789012:role/role-name"
          label="Target IAM Role ARN"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="external_id"
          label="External ID"
          placeholder="1234567890"
          rules={{ required: false }}
        />
      </FormWrapper>
    ),
  },
  aws_user_and_role: {
    display_name: "AWS (User & Role)",
    editor: (
      <FormWrapper<AwsType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="aws_access_key_id"
          placeholder={amazonKeyExample}
          label="Account Id"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="aws_secret_access_key"
          placeholder={amazonKeyExample}
          label="AWS Secret"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="target_role_arn"
          placeholder="arn:aws:iam::123456789012:role/role-name"
          label="Target IAM Role ARN"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="external_id"
          placeholder="1234567890"
          label="External ID"
          rules={{ required: false }}
        />
      </FormWrapper>
    ),
  },
  big_marker: {
    display_name: "BigMarker",
    editor: (
      <FormWrapper<BigMarkerType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  boostr: {
    display_name: "Boostr",
    editor: (
      <FormWrapper<BoostrType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  broadsign: {
    display_name: "Broadsign",
    editor: (
      <FormWrapper<BroadsignType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  cds_bridge_lite: {
    display_name: "CDS Bridge Lite",
    editor: (
      <FormWrapper<CDSType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_token"
          label="API Access Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  celtra: {
    display_name: "Celtra",
    editor: (
      <FormWrapper<CeltraType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="app_id"
          placeholder="1234567890"
          label="App ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="secret_key"
          label="Secret Key"
          placeholder={standardKeyExample}
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  channelmeter: {
    display_name: "ChannelMeter",
    editor: (
      <FormWrapper<ChannelMeterType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  chartbeat: {
    display_name: "Chartbeat",
    editor: (
      <FormWrapper<ChartbeatType>>
        <FormController elementType="textField" type="text" fieldName="host" label="Host" rules={{ required: true }} />
        <FormController
          elementType="textField"
          type="password"
          fieldName="apikey"
          placeholder={standardKeyExample}
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  colossus: {
    display_name: "Colossus",
    editor: (
      <FormWrapper<ColossusType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  commission_junction: {
    display_name: "Commission Junction",
    editor: (
      <FormWrapper<CommissionJunctionType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="token"
          label="Personal Access Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  compus_systems: {
    display_name: "Compu Systems",
    editor: (
      <FormWrapper<CompuSystemsType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="username"
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="event_code"
          placeholder="SOMTH24"
          label="Event Code"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="event_number"
          placeholder="123"
          label="Event Number"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  coupa: {
    display_name: "Coupa",
    editor: (
      <FormWrapper<CoupaType>>
        <FormController
          elementType="textField"
          type="text"
          placeholder="instanceExample"
          fieldName="instance"
          label="Instance"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  connect_ad: {
    display_name: "ConnectAd",
    editor: (
      <FormWrapper<ConnectAdType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  connatix_password: {
    display_name: "Connatix",
    editor: (
      <FormWrapper<ConnatixType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  content_ignite: {
    display_name: "Content Ignite",
    editor: (
      <FormWrapper<ContentIgniteType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  conversant: {
    display_name: "Conversant",
    editor: (
      <FormWrapper<ConversantType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  cpmstar: {
    display_name: "CPMStar",
    editor: (
      <FormWrapper<CPMStarType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="publisher_id"
          placeholder="12345"
          label="Publisher ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="secret_key"
          placeholder={passwordExample}
          label="Secret Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  criteo: {
    display_name: "Criteo",
    editor: (
      <FormWrapper<CriteoType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  criteo_analytics: {
    display_name: "Criteo Analytics",
    editor: (
      <FormWrapper<CriteoAnalyticsType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  criteo_uslicer: {
    display_name: "Criteo Uslicer",
    editor: (
      <FormWrapper<CriteoUslicerType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="slicer_name"
          placeholder="my-slicer"
          label="Slicer Name"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="project_name"
          placeholder="my-project"
          label="Project Name"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_token"
          label="Access Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  customerio: {
    display_name: "Customer.io",
    editor: (
      <FormWrapper<CustomerType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  dianomi_api_key: {
    display_name: "Dionami API Key",
    editor: (
      <FormWrapper<DionamiType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  digital_turbine: {
    display_name: "Digital Turbine",
    editor: (
      <FormWrapper<DigitalTurbineType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="consumer_key"
          label="Consumer Key"
          placeholder={activationKeyExample}
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          placeholder={activationKeyExample}
          type="password"
          fieldName="consumer_secret"
          label="Consumer Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  district_m: {
    display_name: "District M",
    editor: (
      <FormWrapper<DistrictMType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  disqus: {
    display_name: "Disqus",
    editor: (
      <FormWrapper<DisqusType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_token"
          label="Access Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  domo: {
    display_name: "Domo",
    editor: (
      <FormWrapper<DomoType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  double_verify: {
    display_name: "DoubleVerify",
    editor: (
      <FormWrapper<DoubleVerifyType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_token"
          label="API Access Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  equativ: {
    display_name: "Equativ",
    editor: (
      <FormWrapper<EquativType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="username"
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  equativ_oauth: {
    display_name: "Equativ OAuth",
    editor: (
      <FormWrapper<EquativOauthType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  ex_co_api_key: {
    display_name: "EX.CO API Key",
    editor: (
      <FormWrapper<ExCoApiKeyType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          placeholder={activationKeyExample}
          label="API Key"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="account_secret"
          placeholder={standardKeyExample}
          label="Account Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  facebook: {
    display_name: "Facebook",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" buttonType="facebook" isOauth />,
  },
  facebook_marketing_oauth: {
    display_name: "Facebook Marketing",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" buttonType="facebook" isOauth />,
  },
  facebook_pageposts_oauth: {
    display_name: "Meta Page/Posts",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" buttonType="facebook" isOauth />,
  },
  facebook_system_user: {
    display_name: "Meta System User",
    editor: (
      <FormWrapper<FacebookSystemUserType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="name"
          label="System User Name"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_token"
          label="Access Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  first_impression: {
    display_name: "First Impression",
    editor: (
      <FormWrapper<FirstImpressionType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="username"
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  fixer: {
    display_name: "Fixer.io",
    editor: (
      <FormWrapper<FixerType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  flash_talking: {
    display_name: "Flash Talking",
    editor: (
      <FormWrapper<FlashTalkingType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="key"
          placeholder={emailExample}
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="secret"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  flex_offers_key: {
    display_name: "FlexOffers API Key",
    editor: (
      <FormWrapper<FlexOffersType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  fmtc: {
    display_name: "FMTC API Key",
    editor: (
      <FormWrapper<FMTCType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  freewheel: {
    display_name: "Freewheel",
    editor: (
      <FormWrapper<FreewheelType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  freewheel_apikey: {
    display_name: "Freewheel API Key",
    editor: (
      <FormWrapper<FreewheelApiKeyType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  ftp_ssl: {
    display_name: "FTP",
    editor: (
      <FormWrapper<FtpSslType>>
        <FormController elementType="textField" type="text" fieldName="host" label="Host" rules={{ required: true }} />
        <FormController
          elementType="textField"
          type="text"
          fieldName="username"
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
        <FormController elementType="textField" type="text" fieldName="port" label="Port" rules={{ required: false }} />
      </FormWrapper>
    ),
  },
  google_service_account: {
    display_name: "Google Service Account",
    editor: (
      <FormWrapper<GoogleServiceAccountType>>
        <GoogleServiceAccountForm />
      </FormWrapper>
    ),
  },
  google_token: {
    display_name: "Google OAuth (Original)",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth buttonType="google" />,
  },
  google_ads_token_2023: {
    display_name: "Google Ads OAuth",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth buttonType="google" />,
  },
  google_analytics_token_2023: {
    display_name: "Google Analytics OAuth",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth buttonType="google" />,
  },
  google_ad_manager_token_2023: {
    display_name: "Google Ad Manager OAuth",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth buttonType="google" />,
  },
  google_admob_token_2023: {
    display_name: "Google Admob OAuth",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth buttonType="google" />,
  },
  google_adsense_token_2023: {
    display_name: "Google Adsense OAuth",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth buttonType="google" />,
  },
  google_campaign_manager_token: {
    display_name: "Google Campaign Manager OAuth",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth buttonType="google" />,
  },
  google_cloud_platform_token_2023: {
    display_name: "Google Cloud Platform OAuth (BigQuery, Storage)",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth buttonType="google" />,
  },
  google_nonsensitive_token: {
    display_name: "~Google OAuth (Non-sensitive Scopes)",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth buttonType="google" />,
  },
  google_search_analytics_token_2023: {
    display_name: "Google Search Analytics OAuth",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth buttonType="google" />,
  },
  google_search_ads_360_token_2024: {
    display_name: "Google Search Ads 360 OAuth",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth buttonType="google" />,
  },
  google_v1_token: {
    display_name: "~Google OAuth (Under Development)",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth buttonType="google" />,
  },
  gourmet_ads: {
    display_name: "Gourmet Ads",
    editor: (
      <FormWrapper<GourmetAdsType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  graphite_connect: {
    display_name: "Graphite Connect",
    editor: (
      <FormWrapper<GraphiteConnectType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  harvest: {
    display_name: "Harvest",
    editor: (
      <FormWrapper<HarvestType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="account_id"
          label="Account ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_token"
          label="API Access Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  hivestack: {
    display_name: "Hivestack",
    editor: (
      <FormWrapper<HivestackType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_key"
          placeholder={standardKeyExample}
          label="API Access Key"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="secret_key"
          placeholder={standardKeyExample}
          label="API Secret Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  hopin_api_key: {
    display_name: "Hopin API Key",
    editor: (
      <FormWrapper<HopinType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  hubspot: {
    display_name: "Hubspot",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth />,
  },
  hyprMX: {
    display_name: "HyprMX",
    editor: (
      <FormWrapper<HyprMXType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  iconik: {
    display_name: "Iconik",
    editor: (
      <FormWrapper<IconikType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="app_id"
          placeholder="1234567890"
          label="App ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="auth_token"
          placeholder={standardKeyExample}
          label="Auth Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  impact: {
    display_name: "Impact",
    editor: (
      <FormWrapper<ImpactType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="account_sid"
          placeholder="AC1234567890abcdef1234567890abcdef"
          label="Account SID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="auth_token"
          placeholder={standardKeyExample}
          label="Auth Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  improve_digital: {
    display_name: "Improve Digital",
    editor: (
      <FormWrapper<ImproveDigitalType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  index_exchange_audit_log: {
    display_name: "Index Exchange Audit Log",
    editor: (
      <FormWrapper<IndexExchangeAuditLogType>>
        <FormController elementType="textField" type="text" fieldName="name" label="Name" rules={{ required: true }} />
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  index_exchange_reporting: {
    display_name: "Index Exchange Reporting",
    editor: (
      <FormWrapper<IndexExchangeReportingType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="username"
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  infolinks: {
    display_name: "Infolinks Raw Earning Data API",
    editor: (
      <FormWrapper<InfolinksType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  inmobi: {
    display_name: "Inmobi",
    editor: (
      <FormWrapper<InmobiType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="username"
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="secret_key"
          placeholder={passwordExample}
          label="Secret Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  insticator: {
    display_name: "Insticator",
    editor: (
      <FormWrapper<InsticatorType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="account_id"
          label="Account ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  jw_player: {
    display_name: "JW Player",
    editor: (
      <FormWrapper<JWPlayerType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="property_id"
          placeholder="12345678"
          label="Property ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="secret"
          label="Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  kargo_password: {
    display_name: "Kargo",
    editor: (
      <FormWrapper<KargoEmailPasswordType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  kiteworks: {
    display_name: "Kiteworks",
    editor: (
      <FormWrapper<KiteworksType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client Id"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="secret"
          fieldName="secret"
          label="Secret"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="username"
          label="User Name"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },

  klaviyo: {
    display_name: "Klaviyo",
    editor: (
      <FormWrapper<KlaviyoType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },

  kueez: {
    display_name: "Kueez",
    editor: (
      <FormWrapper<KueezType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  kyriba: {
    display_name: "Kyriba",
    editor: (
      <FormWrapper<KyribaType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="username"
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  live_intent: {
    display_name: "Live Intent",
    editor: (
      <FormWrapper<LiveIntentType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="username"
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  linkedin: {
    display_name: "Linkedin",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth />,
  },
  lkqd: {
    display_name: "LKQD",
    editor: (
      <FormWrapper<LKQDType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="key_id"
          label="Key ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="key"
          placeholder={standardKeyExample}
          label="Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  magellan: {
    display_name: "Magellan",
    editor: (
      <FormWrapper>
        <FormController<MagellanType>
          elementType="textField"
          type="text"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  magnite_ctv: {
    display_name: "Magnite CTV",
    editor: (
      <FormWrapper<MagniteCTVType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="key"
          placeholder="ab-123cdeFGH789-ij"
          label="Access Key"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="secret"
          label="Secret Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  magnite_prebid: {
    display_name: "Magnite Prebid",
    editor: (
      <FormWrapper<MagniteType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="key"
          label="Key"
          placeholder={standardKeyExample}
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="secret"
          label="Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  mediagrid_apikey: {
    display_name: "Mediagrid API Key",
    editor: (
      <FormWrapper<MediagridType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  medianet: {
    display_name: "Media.net",
    editor: (
      <FormWrapper<MedianetType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="customer_guid"
          placeholder={emailExample}
          label="Customer GUID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="customer_key"
          placeholder={passwordExample}
          label="Customer Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  medianet_password: {
    display_name: "Media.net: Email & Password",
    editor: (
      <FormWrapper<MedianetEmailPasswordType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  microsoft_token: {
    display_name: "Microsoft Oauth",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth />,
    deprecated: true,
  },
  microsoft_token_v2: {
    display_name: "Microsoft Oauth V2",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth />,
  },
  moat: {
    display_name: "MOAT",
    editor: (
      <FormWrapper<MOATType>>
        <FormController
          elementType="textField"
          type="text"
          placeholder={emailExample}
          fieldName="key"
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="secret"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  moat_token: {
    display_name: "MOAT Token",
    editor: (
      <FormWrapper<MoatTokenType>>
        <FormController elementType="textField" type="text" fieldName="name" label="Name" rules={{ required: true }} />
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  mobfox_ng: {
    display_name: "MobFox",
    editor: (
      <FormWrapper<MobFoxNgType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  mobilefuse: {
    display_name: "MobileFuse",
    editor: (
      <FormWrapper<MobileFuseType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  monday_com_token: {
    display_name: "Monday.com",
    editor: (
      <FormWrapper<MondayType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  mountain: {
    display_name: "MNTN (Mountain)",
    editor: (
      <FormWrapper<MountainType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  nativo_token: {
    display_name: "Nativo",
    editor: (
      <FormWrapper<NativoType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  newsbreak: {
    display_name: "Newsbreak",
    editor: (
      <FormWrapper<NewsbreakType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="token"
          label="Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  next_millennium: {
    display_name: "Next Millennium",
    editor: (
      <FormWrapper<NextMillenniumType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  aditude: {
    display_name: "Aditude",
    editor: (
      <FormWrapper<AditudeType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  vmg_analytics: {
    display_name: "Verizon Media Group Analytics",
    editor: (
      <FormWrapper<VerizonType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  vmg_dsp: {
    display_name: "Verizon Media Group DSP",
    editor: (
      <FormWrapper<VerizonType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  ogury: {
    display_name: "Ogury",
    editor: (
      <FormWrapper<OguryType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  open_water: {
    display_name: "Open Water",
    editor: (
      <FormWrapper<OpenWaterType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_key"
          placeholder={emailExample}
          label="Domain"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          placeholder={activationKeyExample}
          type="password"
          fieldName="api_key"
          label="Api Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  openx_oauth2: {
    display_name: "OpenX OAuth2",
    editor: (
      <FormWrapper<OpenXOauth2Type>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />

        <FormController
          elementType="textField"
          type="text"
          fieldName="hostname"
          placeholder="hostname.com"
          label="Hostname"
          rules={{ required: true }}
        />

        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          placeholder="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  outbrain: {
    display_name: "Outbrain",
    editor: (
      <FormWrapper<OutbrainType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  partnerize: {
    display_name: "Partnerize",
    editor: (
      <FormWrapper<PartnerizeType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="application_key"
          placeholder={standardKeyExample}
          label="Application Key"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="user_api_key"
          placeholder={standardKeyExample}
          label="User API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  pangle: {
    display_name: "Pangle",
    editor: (
      <FormWrapper<PangleType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="user_id"
          placeholder="123456"
          label="User ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="role_id"
          placeholder="123456"
          label="Role ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="secure_key"
          placeholder={standardKeyExample}
          label="Secure Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  pepperjam_publisher: {
    display_name: "PepperJam Publisher",
    editor: (
      <FormWrapper<PepperJamType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  pinterest: {
    display_name: "Pinterest",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth />,
  },
  pinterest_v5: {
    display_name: "Pinterest V5",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth />,
  },
  placements: {
    display_name: "Placements",
    editor: (
      <FormWrapper<PlacementsType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_token"
          label="Access Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  powerinbox_jeeng: {
    display_name: "PowerinboxJeeng",
    editor: (
      <FormWrapper<PowerinboxJeengType>>
        <FormController
          elementType="textField"
          type="text"
          placeholder={activationKeyExample}
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="company_id"
          placeholder={activationKeyExample}
          label="Company ID"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  primis_password: {
    display_name: "Primis",
    editor: (
      <FormWrapper<PrimisType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="username"
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  publica: {
    display_name: "Publica",
    editor: (
      <FormWrapper<PublicaType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  pubmatic: {
    display_name: "Pubmatic",
    editor: (
      <FormWrapper<PubmaticType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  pubmatic_token: {
    display_name: "Pubmatic Token",
    editor: (
      <FormWrapper<PubmaticToken> alertMessage="This key type for Pubmatic has been deprecated and require manual updates every two months. Please switch to 'Pubmatic' key type, which supports automatic refreshes.">
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="refresh_token"
          label="Refresh Token"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_token"
          label="Access Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  pulsepoint: {
    display_name: "PulsePoint",
    editor: (
      <FormWrapper<PulsePointType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  quantcast: {
    display_name: "Quantcast",
    editor: (
      <FormWrapper<QuantcastType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="key"
          label="Key"
          placeholder={standardKeyExample}
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="secret"
          label="Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  reddit: {
    display_name: "Reddit",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth />,
  },
  redshift: {
    display_name: "Redshift",
    editor: <RedshiftEditor />,
  },
  revcontent: {
    display_name: "RevContent",
    editor: (
      <FormWrapper<RevContentType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  rhythm_one: {
    display_name: "Rhythm One",
    editor: (
      <FormWrapper<RhythmOneType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="username"
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  rich_audience: {
    display_name: "Rich Audience",
    editor: (
      <FormWrapper<RichAudienceType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  rise: {
    display_name: "Rise",
    editor: (
      <FormWrapper<RiseType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  rokt: {
    display_name: "ROKT",
    editor: (
      <FormWrapper<RoktType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="app_id"
          placeholder="e.g. 40svbin0d194subpohl079rhck"
          label="App ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="app_secret"
          label="APP Secret"
          placeholder={standardKeyExample}
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  rubicon: {
    display_name: "Rubicon",
    editor: (
      <FormWrapper<RubiconType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="account_id"
          placeholder="12345"
          label="Account ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="key"
          placeholder={standardKeyExample}
          label="Key"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="secret"
          placeholder={standardKeyExample}
          label="Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  sailthru: {
    display_name: "Sailthru",
    editor: (
      <FormWrapper<SailthruType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_secret"
          label="API Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  sailthru_mobile: {
    display_name: "Sailthru Mobile",
    editor: (
      <FormWrapper<SailthruType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_secret"
          label="API Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  salesforce: {
    display_name: "Salesforce",
    editor: (
      <FormWrapper<SalesforceType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="key"
          placeholder={emailExample}
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="secret"
          label="Password"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="sf_token"
          placeholder={standardKeyExample}
          label="Token"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="url"
          placeholder="https://domain.com"
          label="URL"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  salesforce_oauth: {
    display_name: "Salesforce OAuth",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth />,
  },
  salesforce_pardot: {
    display_name: "Salesforce Pardot",
    editor: (
      <FormWrapper<SalesforcePardotType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="user_key"
          placeholder={standardKeyExample}
          label="User Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  samsung_dsp: {
    display_name: "Samsung DSP",
    editor: (
      <FormWrapper<SamsungDSPType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="username"
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  sap_concur: {
    display_name: "SAP Concur",
    editor: (
      <FormWrapper<SapConcurType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="refresh_token"
          label="Refresh Token"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="region"
          placeholder="us2"
          label="Region"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  spotify_ads: {
    display_name: "Spotify Ads",
    editor: (
      <FormWrapper<SpotifyAdsType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="refresh_token"
          label="Refresh Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  serp_robot: {
    display_name: "Serp Robot",
    editor: (
      <FormWrapper<SerpRobotType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  sftp: {
    display_name: "SFTP",
    editor: (
      <FormWrapper<SftpType>>
        <SftpThings />
      </FormWrapper>
    ),
  },
  smilewanted: {
    display_name: "SmileWanted",
    editor: (
      <FormWrapper<SmileWantedType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  share_a_sale: {
    display_name: "ShareASale",
    editor: (
      <FormWrapper<ShareASaleType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_secret"
          label="API Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  sharethrough: {
    display_name: "Sharethrough",
    editor: (
      <FormWrapper<SharethroughType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  show_heroes_new: {
    display_name: "Show Heroes API Token",
    editor: (
      <FormWrapper<ShowHeroesType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="token"
          label="Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  simpli_fi: {
    display_name: "Simpli.Fi",
    editor: (
      <FormWrapper<SimplyFiType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          placeholder={standardKeyExample}
          label="API Key"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="user_api_key"
          placeholder={standardKeyExample}
          label="User API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  skai: {
    display_name: "Skai",
    editor: (
      <FormWrapper<SkaiType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="refresh_token"
          label="Refresh Token"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  skimlinks: {
    display_name: "Skimlinks",
    editor: (
      <FormWrapper<SkimlinksType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="publisher_id"
          placeholder="123456"
          label="Publisher ID"
          rules={{ required: false }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client Id"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  smaato_client: {
    display_name: "Smaato",
    editor: (
      <FormWrapper<SmaatoType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  smaato_token: {
    display_name: "Smaato Token",
    editor: (
      <FormWrapper<SmaatoTokenType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_token"
          label="Access Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  snapchat: {
    display_name: "Snapchat",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth />,
  },
  snapchat_stories: {
    display_name: "Snapchat Stories",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth />,
  },
  snowflake: {
    display_name: "Snowflake",
    editor: (
      <FormWrapper<SnowflakeType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="username"
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          placeholder={standardKeyExample}
          label="Access Token or Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  sonobi: {
    display_name: "Sonobi",
    editor: (
      <FormWrapper<SonobiType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="username"
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  sovrn: {
    display_name: "Sovrn",
    editor: (
      <FormWrapper<SovrnType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="secret_key"
          label="Secret Key"
          placeholder={standardKeyExample}
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  sovrn_advertising: {
    display_name: "Sovrn Advertising Report",
    editor: (
      <FormWrapper<SovrnReportType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  spring_serve: {
    display_name: "Spring Serve",
    editor: (
      <FormWrapper<SpringServeType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  sprout_social: {
    display_name: "Sprout Social",
    editor: (
      <FormWrapper<SproutSocialType>>
        <FormController<SproutSocialType>
          elementType="textField"
          type="password"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  sql: {
    display_name: "SQL",
    editor: <SQLEditor />,
  },
  stripe: {
    display_name: "Stripe",
    editor: (
      <FormWrapper<StripeType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  sublime_skinz: {
    display_name: "Sublime Skinz",
    editor: (
      <FormWrapper<SublimeSkinzType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_secret"
          label="API Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  swoogo: {
    display_name: "Swoogo",
    editor: (
      <FormWrapper<SwoogoType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  taboola: {
    display_name: "Taboola",
    editor: (
      <FormWrapper<TaboolaType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  tappx: {
    display_name: "TappX",
    editor: (
      <FormWrapper<TappXType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="user_id"
          placeholder="123456"
          label="User ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  teads: {
    display_name: "Teads",
    editor: (
      <FormWrapper<TeadsType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_token_secret"
          placeholder={standardKeyExample}
          label="Access Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  the_trade_desk: {
    display_name: "The Trade Desk",
    editor: (
      <FormWrapper<TradeDeskType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  tiktok: {
    display_name: "Tiktok",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth />,
  },
  tiktok_creators: {
    display_name: "~Tiktok creators (op 1)",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth />,
  },
  tiktok_creators_token: {
    display_name: "~Tiktok creators (op 2)",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth />,
  },
  tiktok_creators_1dot0: {
    display_name: "~Tiktok creators (op 3)",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth />,
  },
  triplelift_password: {
    display_name: "Triplelift",
    editor: (
      <FormWrapper<TripleliftType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  trackonomics: {
    display_name: "Trackonomics API",
    editor: (
      <FormWrapper<TrackonomicsType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="system_token"
          label="System Token"
          placeholder={standardKeyExample}
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="text"
          fieldName="env_code"
          label="Env Code"
          placeholder="env_code"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  triton: {
    display_name: "Triton",
    editor: (
      <FormWrapper<AdbookType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="username"
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  twitter: {
    display_name: "Twitter",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth />,
  },
  unruly_report: {
    display_name: "UnrulyReport",
    editor: (
      <FormWrapper<UnrulyReportType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="username"
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  unity_ads_apikey: {
    display_name: "Unity Ads",
    editor: (
      <FormWrapper<UnityAdsType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  venatus_login: {
    display_name: "Venatus Login",
    editor: (
      <FormWrapper<VenatusLoginType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  verve: {
    display_name: "Verve",
    editor: (
      <FormWrapper<VerveType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_token"
          label="API Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  vidazoo: {
    display_name: "Vidazoo",
    editor: (
      <FormWrapper<VidazooType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="access_token"
          label="Access Token"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  video_intelligence: {
    display_name: "Video Intelligence",
    editor: (
      <FormWrapper<VideIntelligenceType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  vistar_media: {
    display_name: "Vistar Media",
    editor: (
      <FormWrapper<VistarMediaType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="username"
          label="Username"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  vungle_apikey: {
    display_name: "Vungle",
    editor: (
      <FormWrapper<VungleType>>
        <FormController
          elementType="textField"
          type="password"
          fieldName="api_key"
          label="API Key"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  webgains: {
    display_name: "Webgains",
    editor: (
      <FormWrapper<WebgainsType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="email"
          label="Email"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="password"
          label="Password"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  yahoo: {
    display_name: "Yahoo Report",
    editor: (
      <FormWrapper<YahooType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  yieldmo: {
    display_name: "Yieldmo",
    editor: (
      <FormWrapper<YieldmoType>>
        <FormController
          elementType="textField"
          type="text"
          fieldName="client_id"
          label="Client ID"
          rules={{ required: true }}
        />
        <FormController
          elementType="textField"
          type="password"
          fieldName="client_secret"
          label="Client Secret"
          rules={{ required: true }}
        />
      </FormWrapper>
    ),
  },
  youtube_token_2023: {
    display_name: "Youtube OAuth",
    isOauth: true,
    editor: <FormWrapper buttonText="Connect" isOauth buttonType="youtube" />,
  },
};

interface CredentialTypeDefinition {
  display_name: string;
  editor: (typeof CREDENTIAL_TYPES)[keyof typeof CREDENTIAL_TYPES]["editor"];
  isOauth?: boolean;
  deprecated?: boolean;
}

/** For lookups by a credential type string from the API, which may not name a type the UI knows. */
const CREDENTIAL_TYPES_BY_NAME: Record<string, CredentialTypeDefinition | undefined> = CREDENTIAL_TYPES;

export { CREDENTIAL_TYPES, CREDENTIAL_TYPES_BY_NAME };
export type { CredentialTypeDefinition };
